/** Diver profile stores certifications as comma-separated text (sometimes JSON). */
export function parseParticipantCertifications(
  raw: string | string[] | null | undefined
): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.map((c) => String(c).trim()).filter(Boolean);
      }
    } catch {
      // fall through to comma split
    }
  }
  return trimmed
    .split(/[,;]/)
    .map((c) => c.trim())
    .filter(Boolean);
}

/** Values that mean "no certification" (lowercase, punctuation stripped, single spaces). */
const NO_CERTIFICATION_VALUES = new Set([
  "",
  "none",
  "no certification",
  "no certification needed",
  "no certification required",
  "not required",
  "n a",
  "na",
]);

function normalizeCertificationValue(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** True for empty values and "None" / "No certification (needed)" / "Not required" / "N/A". */
export function isNoCertificationValue(value: string | null | undefined): boolean {
  return NO_CERTIFICATION_VALUES.has(normalizeCertificationValue(value));
}

/**
 * A minimum certification is set on the listing/liveaboard.
 * Any real value (Open Water, AOW, Tec 40...) counts; "None", "No Certification Needed", etc. do not.
 */
export function hasMinimumCertification(
  minCertification: string | null | undefined
): boolean {
  return !isNoCertificationValue(minCertification);
}

/**
 * When a listing sets a minimum certification, "No certification" is not allowed.
 * Beginner categories (Open Water course, Intro, Snorkel) keep the old behavior.
 */
export function listingRequiresCertification(listing: {
  minCertification?: string | null;
  category?: string | null;
}): boolean {
  const category = listing.category ?? "";
  return (
    hasMinimumCertification(listing.minCertification) &&
    category !== "Open Water" &&
    category !== "Intro" &&
    category !== "Snorkel"
  );
}

/** True when the participant picked at least one real certification (not only "No certification"). */
export function hasRealCertification(
  raw: string | string[] | null | undefined
): boolean {
  return parseParticipantCertifications(raw).some(
    (cert) => !isNoCertificationValue(cert)
  );
}

export function missingCertificationMessage(
  personNumber: number,
  minCertification: string | null | undefined
): string {
  return `Person ${personNumber} needs a certification: this experience requires ${(minCertification ?? "").trim()}`;
}

/**
 * Error for the first participant without a real certification, or null.
 * Participants are numbered 1..n in the order given.
 */
export function findMissingCertificationError(
  minCertification: string | null | undefined,
  participants: ReadonlyArray<
    { certifications?: string | string[] | null } | null | undefined
  >
): string | null {
  for (let i = 0; i < participants.length; i++) {
    if (!hasRealCertification(participants[i]?.certifications)) {
      return missingCertificationMessage(i + 1, minCertification);
    }
  }
  return null;
}

/** Categories a complete beginner can book without any certification. */
export const BEGINNER_CATEGORIES = ["Intro", "Snorkel", "Open Water"] as const;

/** True for the categories a diver with no certification can book (Intro, Snorkel, Open Water course). */
export function isBeginnerCategory(category: string | null | undefined): boolean {
  const normalized = (category ?? "").trim().toLowerCase();
  return BEGINNER_CATEGORIES.some((c) => c.toLowerCase() === normalized);
}

/** DB values that mean "no certification"; used to filter listings a beginner can book. */
export const NO_CERTIFICATION_DB_VALUES = [
  "",
  "None",
  "No certification",
  "No Certification Needed",
  "No certification required",
  "Not required",
  "N/A",
  "NA",
];

/** Short display names for the badge; dive centers type these free-hand. */
const CERTIFICATION_LABEL_ALIASES: Array<[RegExp, string]> = [
  [/^ow$/i, "Open Water"],
  [/^owd$/i, "Open Water"],
  [/^open water diver$/i, "Open Water"],
  [/^aow$/i, "Advanced OW"],
  [/^aowd$/i, "Advanced OW"],
  [/^advanced open water( diver)?$/i, "Advanced OW"],
  [/^advanced$/i, "Advanced OW"],
  [/^rescue( diver)?$/i, "Rescue"],
  [/^divemaster( dm)?$/i, "Divemaster"],
  [/^dm$/i, "Divemaster"],
];

/** "OW" → "Open Water", "AOW" → "Advanced OW", "Rescue Diver" → "Rescue". Keeps anything else as typed. */
export function normalizeCertificationLabel(
  value: string | null | undefined
): string {
  const raw = (value ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return "";
  for (const [pattern, label] of CERTIFICATION_LABEL_ALIASES) {
    if (pattern.test(raw)) return label;
  }
  return raw;
}

export type CertificationBadge = {
  /** "none" = green "No experience needed"; "required" = neutral "<cert> required". */
  kind: "none" | "required";
  label: string;
};

/**
 * Badge shown on listing cards and on the listing page so a beginner sees the
 * requirement before booking. Returns null when there is nothing useful to say
 * (no minimum certification set on a non-beginner category).
 */
export function getListingCertificationBadge(listing: {
  minCertification?: string | null;
  category?: string | null;
}): CertificationBadge | null {
  if (hasMinimumCertification(listing.minCertification)) {
    return {
      kind: "required",
      label: `${normalizeCertificationLabel(listing.minCertification)} required`,
    };
  }
  if (isBeginnerCategory(listing.category)) {
    return { kind: "none", label: "No experience needed" };
  }
  return null;
}
